// Sign-in token (SEC-13). The login gate in front of n8n gives one to every signed-in user and
// guest. It rides on every call to our n8n web addresses and the OCR service, so the server can
// check that the user id in a request belongs to whoever is signed in.
//
// Imported first in src/index.js: the wrapper must be on window.fetch before any other module
// keeps its own reference to fetch (errorLog.js does).
import { OCR_BASE_URL } from '../features/knowledge-bases/ocrConfig';

const N8N_BASE_URL = process.env.REACT_APP_N8N_BASE_URL || 'http://localhost:5678';
const KEY = 'authToken';

// Fired when the gate says the session has ended; AppContext signs the user out.
export const AUTH_EXPIRED_EVENT = 'moe:auth-expired';

export const getAuthToken = () => {
  try {
    return localStorage.getItem(KEY) || '';
  } catch {
    return '';
  }
};

export const setAuthToken = (token) => {
  try {
    if (token) localStorage.setItem(KEY, token);
    else localStorage.removeItem(KEY);
  } catch {
    // Storage blocked: requests simply go without a token.
  }
};

export const clearAuthToken = () => setAuthToken('');

// Image tags cannot send headers, so page images carry the token in the address instead.
export const withAuthQuery = (url) => {
  const token = getAuthToken();
  return token ? `${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : url;
};

const isOurs = (url) =>
  typeof url === 'string' && (url.startsWith(`${N8N_BASE_URL}/webhook/`) || url.startsWith(`${OCR_BASE_URL}/`));

const originalFetch = window.fetch.bind(window);

window.fetch = async (input, init = {}) => {
  const url = typeof input === 'string' ? input : input?.url;
  const token = getAuthToken();
  if (token && isOurs(url)) {
    const headers = new Headers(init.headers || (typeof input === 'string' ? undefined : input.headers));
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    init = { ...init, headers };
  }
  const response = await originalFetch(input, init);
  if (response.status === 401 && isOurs(url)) {
    response.clone().json()
      .then((data) => { if (data?.code === 'auth_required') window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT)); })
      .catch(() => {});
  }
  return response;
};
